import { useCallback, useEffect, useState } from "react";
import type { AiPrice, PriceSyncStatus, ApiKeyStatus, BotStatus, CampaignSummary, KeyProvider, MyKeysResponse, UpdateStatus } from "@dm/shared";
import { api } from "./api.js";
import { useApp } from "./appContext.js";

type Tab = "xai" | "elevenlabs" | "account" | "bot" | "server-key" | "prices" | "updates";
const TABS: Array<{ id: Tab; label: string; admin?: boolean }> = [
  { id: "xai", label: "xAI" },
  { id: "elevenlabs", label: "ElevenLabs" },
  { id: "account", label: "Account" },
  { id: "bot", label: "Discord bot", admin: true },
  { id: "server-key", label: "Server key", admin: true },
  { id: "prices", label: "AI prices", admin: true },
  { id: "updates", label: "Updates", admin: true },
];

export const settingsTab = (hash: string): Tab => {
  const m = /^#\/settings\/([\w-]+)/.exec(hash);
  return (TABS.find((t) => t.id === m?.[1])?.id ?? "xai") as Tab;
};

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "never");
const money = (n: number) => (n < 0.01 && n > 0 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`);

/** Settings: your own API keys and account; Admin-only server pages. */
export function SettingsPage({ tab }: { tab: Tab }) {
  const { me } = useApp();
  const visible = TABS.filter((t) => !t.admin || me.isAdmin);
  const current = visible.some((t) => t.id === tab) ? tab : "xai";
  return (
    <main className="settings">
      <nav className="subtabs">
        {visible.map((t) => (
          <a key={t.id} href={`#/settings/${t.id}`} className={t.id === current ? "active" : ""}>
            {t.label}
          </a>
        ))}
      </nav>
      {current === "xai" && <KeyPanel provider="xai" />}
      {current === "elevenlabs" && <KeyPanel provider="elevenlabs" />}
      {current === "account" && <AccountPanel />}
      {current === "bot" && <BotPanel />}
      {current === "server-key" && <ServerKeyPanel />}
      {current === "prices" && <PricesPanel />}
      {current === "updates" && <UpdatesPanel />}
    </main>
  );
}

function useRun() {
  const { fail } = useApp();
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }, [fail]);
  return { busy, run };
}

// ---------------------------------------------------------------- API keys

const PROVIDER_INFO: Record<KeyProvider, { name: string; where: string; use: string }> = {
  xai: {
    name: "xAI",
    where: "https://console.x.ai",
    use: "Runs the DM in every campaign you host: the voice DM, its speech, the memory scribe and private whisper replies. Co-hosts always use your key.",
  },
  elevenlabs: {
    name: "ElevenLabs",
    where: "https://elevenlabs.io/app/settings/api-keys",
    use: "Stored for ElevenLabs voices, which arrive in a later update (Phase 5). Nothing uses it yet.",
  },
};

function KeyPanel({ provider }: { provider: KeyProvider }) {
  const { busy, run } = useRun();
  const [data, setData] = useState<MyKeysResponse | null>(null);
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const load = useCallback(() => run(async () => setData(await api.myKeys())), [run]);
  useEffect(() => void load(), [load]);
  const info = PROVIDER_INFO[provider];
  const k: ApiKeyStatus | undefined = data?.keys.find((x) => x.provider === provider);
  const save = () => run(async () => {
    await api.saveKey(provider, draft.trim());
    setDraft("");
    const tested = await api.testKey(provider);
    setNote(tested.status === "ok" ? "Saved and tested: the key works." : `Saved, but the test failed: ${tested.lastError ?? "unknown error"}`);
    setData(await api.myKeys());
  });
  return (
    <section className="panel">
      <h3>{info.name} API key</h3>
      <p className="muted">{info.use}</p>
      {data && !data.encryptionReady && (
        <p className="error">Saving keys is off: the server has no ENCRYPTION_KEY yet. Ask the Admin.</p>
      )}
      {k?.set ? (
        <p>
          Saved key: <code>…{k.last4}</code>{" "}
          <span className={`tag ${k.status === "ok" ? "ok" : k.status === "failed" ? "danger" : ""}`}>
            {k.status === "ok" ? "works" : k.status === "failed" ? "failed" : "not tested"}
          </span>
          <span className="muted small"> · tested {when(k.testedAt)}</span>
          {k.lastError && <span className="error small"><br />{k.lastError}</span>}
        </p>
      ) : (
        <p className="muted">No key saved.</p>
      )}
      <div className="row">
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder={k?.set ? "Paste a new key to replace it" : `Paste your ${info.name} API key`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{ flex: 1 }}
        />
        <button className="primary" disabled={busy || draft.trim().length < 16 || !data?.encryptionReady} onClick={save}>Save</button>
      </div>
      <div className="row">
        <button disabled={busy || !k?.set} onClick={() => run(async () => {
          const t = await api.testKey(provider);
          setNote(t.status === "ok" ? "The key works." : `Test failed: ${t.lastError ?? "unknown error"}`);
          setData(await api.myKeys());
        })}>Test key</button>
        <button className="danger" disabled={busy || !k?.set} onClick={() => window.confirm(`Remove your ${info.name} key?`) && run(async () => {
          await api.deleteKey(provider);
          setNote("Key removed.");
          setData(await api.myKeys());
        })}>Remove</button>
      </div>
      {note && <p className="note">{note}</p>}
      <p className="muted small">
        Get a key at <a href={info.where} target="_blank" rel="noreferrer">{info.where}</a>. Keys are encrypted on the server and never shown again (only the last 4 characters).
      </p>
      {provider === "xai" && data && data.campaigns.length > 0 && (
        <>
          <h4>Campaigns you host</h4>
          <table className="simple">
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>
                    {c.keySource === "host" ? "Your key" : c.keySource === "server" ? "Server key (allowed by the Admin)" : <span className="error">No key: the DM can't run</span>}
                    {c.keyFailure && <span className="error small"><br />Paused: {c.keyFailure}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- account (Discord; email deferred)

function AccountPanel() {
  const { me, reloadMe } = useApp();
  const { busy, run } = useRun();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [options, setOptions] = useState<{ email: boolean } | null>(null);
  useEffect(() => void api.authOptions().then(setOptions, () => setOptions({ email: false })), []);
  const params = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
  return (
    <section className="panel">
      <h3>Account</h3>
      {params.get("email_verified") && <p className="note">Email confirmed. You can now log in with an emailed link too.</p>}
      {params.get("linked") && <p className="note">Discord linked. You can play in voice now.</p>}
      {params.get("link_error") === "discord_taken" && (
        <p className="error">That Discord account already has its own account here. Log in with Discord instead and add this email under Settings → Account.</p>
      )}
      <p>
        <strong>Discord:</strong>{" "}
        {me.hasDiscord ? (
          <>linked as {me.user.username}</>
        ) : (
          <>
            not linked. Voice play needs Discord.{" "}
            {options?.email && <a className="button" href="/auth/discord/login?link=1">Link Discord</a>}
          </>
        )}
      </p>
      {/* Email login is deferred (Discord only): this part only shows if the server turns it back on. */}
      {options?.email && (
        <>
          <p>
            <strong>Email:</strong> {me.email ?? <span className="muted">none</span>}
            {me.email && me.hasDiscord && (
              <button className="link danger" disabled={busy} onClick={() => run(async () => {
                await api.removeEmail();
                await reloadMe();
                setNote("Email removed.");
              })}> Remove</button>
            )}
          </p>
          <div className="row">
            <input type="email" placeholder={me.email ? "New email address" : "Add an email address"} value={email} onChange={(e) => setEmail(e.target.value)} style={{ flex: 1 }} />
            <button className="primary" disabled={busy || !email.includes("@")} onClick={() => run(async () => {
              await api.addEmail(email.trim());
              setNote(`Check ${email.trim()} for a confirmation link (valid 15 minutes).`);
              setEmail("");
            })}>Send confirmation</button>
          </div>
        </>
      )}
      {note && <p className="note">{note}</p>}
    </section>
  );
}

// ---------------------------------------------------------------- Admin: Discord bot

function BotPanel() {
  const { busy, run } = useRun();
  const [s, setS] = useState<BotStatus | null>(null);
  useEffect(() => void run(async () => setS(await api.botStatus())), [run]);
  return (
    <section className="panel">
      <h3>Discord bot</h3>
      {!s ? <p className="muted">Loading…</p> : (
        <>
          <p>
            Status: <span className={`tag ${s.online ? "ok" : "danger"}`}>{s.online ? "online" : "offline"}</span>
            <span className="muted small"> · last heartbeat {when(s.seenAt)}{s.startedAt ? ` · running since ${when(s.startedAt)}` : ""}</span>
          </p>
          {s.tag && <p>Logged in as <strong>{s.tag}</strong></p>}
          <p><strong>Servers ({s.guilds.length}):</strong> {s.guilds.map((g) => g.name).join(", ") || "none"}</p>
          <p><strong>In voice now:</strong> {s.voiceSessions.length ? s.voiceSessions.map((v) => `#${v.channelName} (campaign ${v.campaignId})`).join(", ") : "nowhere"}</p>
          <h4>Invite the bot to another Discord server</h4>
          {s.inviteUrl ? (
            <>
              <p><a className="button" href={s.inviteUrl} target="_blank" rel="noreferrer">Open invite link</a></p>
              <p className="muted small">
                Permissions: View Channels, Send Messages, Read Message History, Connect, Speak, Use Voice Activity. After it joins, link a voice channel in Campaign settings or type /join there.
                Slash commands register automatically only in the main server (DISCORD_GUILD_ID).
              </p>
            </>
          ) : <p className="muted">No client id configured.</p>}
          <button disabled={busy} onClick={() => run(async () => setS(await api.botStatus()))}>Refresh</button>
          <p className="muted small">The bot's token stays in the server's .env; it is never shown here.</p>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Admin: server-key fallback

function ServerKeyPanel() {
  const { busy, run } = useRun();
  const [list, setList] = useState<CampaignSummary[]>([]);
  const load = useCallback(() => run(async () => {
    const r = await api.campaigns();
    setList([...r.mine, ...r.public].sort((a, b) => a.campaign.name.localeCompare(b.campaign.name)));
  }), [run]);
  useEffect(() => void load(), [load]);
  return (
    <section className="panel">
      <h3>Server key fallback</h3>
      <p className="muted">
        Each campaign runs on its Host's own xAI key. Only when a Host has no key can a campaign use the server's key (XAI_API_KEY in .env), and only if you switch it on here.
        A failing Host key never falls back to the server key.
      </p>
      <table className="simple">
        <thead><tr><th>Campaign</th><th>Host</th><th>Use server key</th></tr></thead>
        <tbody>
          {list.map(({ campaign: c }) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td>{c.ownerName ?? "—"}</td>
              <td>
                <label className="switch">
                  <input type="checkbox" checked={c.useServerKey && c.hostCanUseServerKey !== false} disabled={busy || (c.hostCanUseServerKey === false && !c.useServerKey)} onChange={(e) => run(async () => {
                    await api.setServerKey(c.id, e.target.checked);
                    await load();
                  })} /> {c.useServerKey && c.hostCanUseServerKey !== false ? "On" : "Off"}
                </label>
                {c.hostCanUseServerKey === false && <span className="muted small"><br />Host isn't allowed: tick “Can use server key” for them in Admin → Accounts.</span>}
                {c.keyFailure && <span className="error small"><br />{c.keyFailure}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ---------------------------------------------------------------- Admin: prices

const KIND_LABELS: Record<AiPrice["kind"], string> = {
  input: "Input tokens", cached_input: "Cached input tokens", output: "Output tokens (incl. reasoning)",
  input_long: "Input (≥200k prompt)", cached_input_long: "Cached input (≥200k prompt)", output_long: "Output (≥200k prompt)",
  tts_chars: "TTS characters",
  image: "Images (per 1M images)",
  image_input: "Reference images in (per 1M)",
};

function PricesPanel() {
  const { busy, run } = useRun();
  const [prices, setPrices] = useState<AiPrice[]>([]);
  const [edit, setEdit] = useState<Record<number, string>>({});
  const [add, setAdd] = useState({ provider: "xai", model: "", kind: "input" as AiPrice["kind"], usd: "" });
  const [sync, setSync] = useState<PriceSyncStatus | null>(null);
  useEffect(() => void run(async () => { setPrices((await api.prices()).prices); setSync(await api.priceSync()); }), [run]);
  const save = (p: Pick<AiPrice, "provider" | "model" | "kind">, usd: string, source?: string) => run(async () => {
    const v = usd.trim() === "" ? null : Number(usd);
    setPrices((await api.savePrice({ ...p, usdPerMillion: v, source })).prices);
  });
  return (
    <section className="panel">
      <h3>AI prices</h3>
      <p className="muted">USD per 1 million tokens (or characters for speech, or images for pictures: $0.02 per image is 20000). Costs are worked out from this table; xAI's own billing is the final word.
        Seeded from xAI's published prices (docs.x.ai, October 2026). A model with no row here is tracked but shown as "unpriced".</p>
      <table className="simple">
        <thead><tr><th>Provider</th><th>Model</th><th>What</th><th>$ / 1M</th><th>Source</th><th title="Locked rows are never changed by the weekly automatic check. Saving a price locks it.">Lock</th><th /></tr></thead>
        <tbody>
          {prices.map((p) => (
            <tr key={p.id} className={p.placeholder ? "placeholder" : ""}>
              <td>{p.provider}</td>
              <td>{p.model === "*" ? "any" : p.model}</td>
              <td>{KIND_LABELS[p.kind]}</td>
              <td>
                <input style={{ width: "6em" }} value={edit[p.id] ?? (p.usdPerMillion ?? "").toString()} placeholder="not set"
                  onChange={(e) => setEdit({ ...edit, [p.id]: e.target.value })} />
                {p.placeholder && <span className="tag danger">placeholder</span>}
                {(p.kind === "image" || p.kind === "image_input") && p.usdPerMillion !== null && <span className="muted small"> = ${(p.usdPerMillion / 1_000_000).toFixed(3)} per image</span>}
              </td>
              <td className="small muted">{p.source}{p.updatedByName ? ` · ${p.updatedByName}` : ""}</td>
              <td>{p.provider === "xai" && p.model !== "*" && (
                <input type="checkbox" aria-label="Lock against automatic updates" checked={!!p.locked} disabled={busy}
                  onChange={(e) => run(async () => setPrices((await api.lockPrice(p.id, e.target.checked)).prices))} />)}</td>
              <td>
                <button disabled={busy || edit[p.id] === undefined} onClick={() => void save(p, edit[p.id]!, "set by the Admin").then(() => setEdit((e) => { const n = { ...e }; delete n[p.id]; return n; }))}>Save</button>
                <button className="link danger" disabled={busy} onClick={() => window.confirm(`Delete the ${p.provider} ${p.model === "*" ? "(any model)" : p.model} ${KIND_LABELS[p.kind]} price? Usage it covers will show as "unpriced". (Seeded prices can be put back with "Restore missing defaults".)`) && run(async () => setPrices((await api.deletePrice(p.id)).prices))}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        <button disabled={busy} title="Put back any of the original xAI prices that were deleted. Prices you edited or added are left alone."
          onClick={() => run(async () => { const r = await api.restoreDefaultPrices(); setPrices(r.prices); window.alert(r.restored ? `Restored ${r.restored} missing default price${r.restored === 1 ? "" : "s"}.` : "All default prices are already there."); })}>
          Restore missing defaults
        </button>
      </p>
      <h4>Automatic price check</h4>
      <p className="muted small">Every Monday at 3:00 AM (New York time) the server reads xAI's price list with the server key and updates xAI rows whose price changed.
        Locked rows are skipped, nothing is deleted, and the check is skipped if xAI can't be read or a price looks wrong (zero, negative, or a change of more than 5x). It waits if a session is open.</p>
      <p>
        Last checked: {sync?.lastCheckedAt ? new Date(sync.lastCheckedAt).toLocaleString() : "never"}{sync?.lastResult ? ` (${sync.lastResult})` : ""}
        {sync && <span className="muted small"> · next {new Date(sync.nextRunAt).toLocaleString()}</span>}{" "}
        <button disabled={busy} onClick={() => run(async () => { const r = await api.checkPricesNow(); setPrices(r.prices); setSync(r.status); window.alert(r.result); })}>Check now</button>
      </p>
      {sync && sync.changes.length > 0 && (
        <details>
          <summary>Automatic changes ({sync.changes.length})</summary>
          <table className="simple">
            <thead><tr><th>When</th><th>Model</th><th>What</th><th>Old</th><th>New</th></tr></thead>
            <tbody>{sync.changes.map((c) => (
              <tr key={c.id}><td>{new Date(c.at).toLocaleString()}</td><td>{c.model}</td><td>{KIND_LABELS[c.kind]}</td><td>{c.oldUsd ?? "not set"}</td><td>{c.newUsd}</td></tr>
            ))}</tbody>
          </table>
        </details>
      )}
      <h4>Add a price</h4>
      <div className="row">
        <input style={{ width: "6em" }} value={add.provider} onChange={(e) => setAdd({ ...add, provider: e.target.value })} placeholder="xai" />
        <input value={add.model} onChange={(e) => setAdd({ ...add, model: e.target.value })} placeholder="model (empty = any)" />
        <select value={add.kind} onChange={(e) => setAdd({ ...add, kind: e.target.value as AiPrice["kind"] })}>
          {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input style={{ width: "6em" }} value={add.usd} onChange={(e) => setAdd({ ...add, usd: e.target.value })} placeholder="$ / 1M" />
        <button className="primary" disabled={busy || !add.provider} onClick={() => void save({ provider: add.provider, model: add.model || "*", kind: add.kind }, add.usd, "set by the Admin").then(() => setAdd({ ...add, model: "", usd: "" }))}>Add</button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- Admin: updates

function UpdatesPanel() {
  const { busy, run } = useRun();
  const [s, setS] = useState<UpdateStatus | null>(null);
  useEffect(() => {
    void run(async () => setS(await api.updateStatus()));
    const t = setInterval(() => void api.updateStatus().then(setS, () => undefined), 10_000);
    return () => clearInterval(t);
  }, [run]);
  return (
    <section className="panel">
      <h3>Updates</h3>
      <p className="muted">
        Every day at 5:00 AM Eastern the server checks GitHub for new versions. If a game is running, the DM announces a 10-minute countdown, then the session is saved and closed and the update installs.
        If nobody is playing it installs straight away. Only the server, bot and speech services restart; the database is untouched.
      </p>
      {s && (
        <>
          <p>
            Now: <strong>{s.phase === "idle" ? "idle" : s.phase === "countdown" ? `countdown until ${when(s.deadline)}` : s.phase}</strong>
            {s.message && <span className="muted"> · {s.message}</span>}
          </p>
          <p className="small">Last check: {when(s.lastCheckAt)} · Last result: {s.lastResult ?? "none yet"}</p>
          <p className="small">
            Updater container: {s.updaterSeenAt ? `last seen ${when(s.updaterSeenAt)}` : "not seen yet"}
            {s.updaterProblem && <span className="error"><br />{s.updaterProblem}</span>}
          </p>
        </>
      )}
      <div className="row">
        <button disabled={busy} onClick={() => run(async () => setS(await api.updateCheck()))}>Check now</button>
        <button className="danger" disabled={busy} onClick={() => window.confirm("Save and end every running session now, then update?") && run(async () => setS(await api.updateNow()))}>
          Update now
        </button>
        {s?.phase === "countdown" && <button disabled={busy} onClick={() => run(async () => setS(await api.updateCancel()))}>Cancel countdown</button>}
      </div>
      <p className="muted small">"Update now" ends all sessions immediately (the scribe saves them first), then pulls and restarts.</p>
    </section>
  );
}

export { money };
